import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { 
  Camera, 
  User, 
  Building, 
  FileText, 
  Car, 
  Phone, 
  MessageSquare, 
  Loader2, 
  X,
  ArrowLeft,
  Save
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { insertarVisitante, InsertarVisitanteRequest } from "../../../../api/libroVisitasService";
import { obtenerTiposDocumento, TipoDocumento } from "../../../../api/tipoDocumentoService";
import { obtenerTiposVehiculo, TipoVehiculo } from "../../../../api/tipoVehiculoService";
import Swal from "sweetalert2";

export default function CreateVisitorPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const unidadId = session?.unidad?.id || "";
  
  // Estados del formulario
  const [nombreCompleto, setNombreCompleto] = useState("");
  const [torre, setTorre] = useState("");
  const [apartamento, setApartamento] = useState("");
  const [tipoDocumentoCodigo, setTipoDocumentoCodigo] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [tipoVehiculo, setTipoVehiculo] = useState("");
  const [placaVehiculo, setPlacaVehiculo] = useState("");
  const [celular, setCelular] = useState("");
  const [motivoVisita, setMotivoVisita] = useState("");
  const [fotoBase64, setFotoBase64] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  
  // Estados para catálogos
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [tiposVehiculo, setTiposVehiculo] = useState<TipoVehiculo[]>([]);
  const [isLoadingCatalogos, setIsLoadingCatalogos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estados para cámara
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Estados de validación
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    loadCatalogos();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCatalogos = async () => {
    setIsLoadingCatalogos(true);
    try {
      const docResponse = await obtenerTiposDocumento();
      if (docResponse.code === "1" && docResponse.object_) {
        setTiposDocumento(docResponse.object_);
      }

      const vehResponse = await obtenerTiposVehiculo();
      if (vehResponse.code === "1" && vehResponse.object_) {
        setTiposVehiculo(vehResponse.object_);
      }
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los catálogos. Por favor, recarga la página.",
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsLoadingCatalogos(false);
    }
  };

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("La API de cámara no está disponible en este navegador.");
      }

      // Mostramos primero el modal para asegurarnos de que el <video> exista
      setShowCamera(true);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      setStream(mediaStream);
    } catch (error) {
      console.error("Error al acceder a la cámara:", error);
      Swal.fire({
        icon: "error",
        title: "Error de cámara",
        text:
          "No se pudo acceder a la cámara. Verifica los permisos del navegador y que la cámara no esté siendo usada por otra aplicación.",
        confirmButtonColor: "#1a3c34",
      });
    }
  };

  // Cuando el modal está visible y tenemos un stream, lo conectamos al <video>
  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;

      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.error("No se pudo reproducir el video de la cámara:", err);
        });
      }
    }
  }, [showCamera, stream]);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      if (context) {
        context.drawImage(video, 0, 0);
        const base64String = canvas.toDataURL('image/jpeg', 0.8);
        setFotoBase64(base64String);
        setPreviewImage(base64String);
        stopCamera();
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: "error",
        title: "Archivo inválido",
        text: "Por favor, selecciona una imagen válida.",
        confirmButtonColor: "#1a3c34",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "Archivo muy grande",
        text: "La imagen no debe superar los 5MB.",
        confirmButtonColor: "#1a3c34",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setFotoBase64(base64String);
      setPreviewImage(base64String);
    };
    reader.readAsDataURL(file);
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!nombreCompleto.trim()) {
      newErrors.nombreCompleto = "El nombre completo es obligatorio";
    }

    if (!torre.trim()) {
      newErrors.torre = "La torre es obligatoria";
    }

    if (!apartamento.trim()) {
      newErrors.apartamento = "El apartamento es obligatorio";
    }

    if (!motivoVisita.trim()) {
      newErrors.motivoVisita = "El motivo de la visita es obligatorio";
    }

    if (!celular.trim()) {
      newErrors.celular = "El número de celular es obligatorio";
    } else if (!/^[0-9]{10}$/.test(celular.replace(/\s/g, ''))) {
      newErrors.celular = "El celular debe tener 10 dígitos";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Por favor, completa todos los campos obligatorios.",
        confirmButtonColor: "#1a3c34",
      });
      return;
    }

    if (!unidadId) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se encontró el código de la unidad residencial.",
        confirmButtonColor: "#1a3c34",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: InsertarVisitanteRequest = {
        "unidad residencial codigo": unidadId,
        "nombre visitante": nombreCompleto.trim(),
        "torre": torre.trim(),
        "apartamento": apartamento.trim(),
        "motivo visita": motivoVisita.trim(),
        "numero celular": celular.trim(),
      };

      if (tipoDocumentoCodigo) {
        payload["tipo documento codigo"] = tipoDocumentoCodigo;
      }
      if (numeroDocumento.trim()) {
        payload["nuemro documento"] = numeroDocumento.trim();
      }
      if (tipoVehiculo) {
        payload["tipo vehiculo"] = tipoVehiculo;
      }
      if (placaVehiculo.trim()) {
        payload["placa vehiculo"] = placaVehiculo.trim();
      }
      if (fotoBase64) {
        payload["foto_base64"] = fotoBase64;
      }

      const response = await insertarVisitante(payload);

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Visitante registrado!",
          text: response.message || "El visitante ha sido registrado exitosamente.",
          confirmButtonColor: "#1a3c34",
        });
        navigate("/visitantes");
      } else {
        throw new Error(response.message || "Error al registrar el visitante");
      }
    } catch (error) {
      console.error("Error al registrar visitante:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error instanceof Error ? error.message : "Error al registrar el visitante. Por favor, intenta nuevamente.",
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900 w-full overflow-x-hidden">
      <Sidebar />
      
      <main className="flex-1 p-3 sm:p-4 md:p-6 md:ml-64 transition-all w-full max-w-full overflow-x-hidden">
        <div className="mb-4 sm:mb-6">
          <Button
            onClick={() => navigate("/visitantes")}
            variant="outline"
            className="mb-3 sm:mb-4 text-sm sm:text-base"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-military-600 break-words">
            Agregar Visitante
          </h2>
        </div>

        <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white w-full">
          <CardHeader className="p-3 sm:p-4 md:p-6">
            <CardTitle className="text-base sm:text-lg md:text-xl text-military-600">
              Información del Visitante
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0 sm:pt-0 md:pt-0">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              {/* Foto del visitante */}
              <div className="space-y-3">
                <Label className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <Camera className="h-4 w-4 sm:h-5 sm:w-5" />
                  Foto del Visitante
                </Label>
                
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-6 md:p-8 text-center hover:border-military-400 hover:bg-gray-50 transition-all">
                  {previewImage ? (
                    <div className="relative inline-block mb-4 group">
                      <img
                        src={previewImage}
                        alt="Preview"
                        className="max-h-48 sm:max-h-64 md:max-h-72 max-w-full rounded-xl shadow-lg object-contain border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewImage("");
                          setFotoBase64("");
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all shadow-xl hover:scale-110 z-10"
                        title="Eliminar imagen"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3 sm:space-y-4">
                      <Camera className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-gray-400" />
                      <p className="text-xs sm:text-sm text-gray-600">
                        No hay imagen seleccionada
                      </p>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center mt-4">
                    <Button
                      type="button"
                      onClick={startCamera}
                      variant="outline"
                      className="flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      <Camera className="h-4 w-4" />
                      Tomar Foto
                    </Button>
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      className="flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      <FileText className="h-4 w-4" />
                      Seleccionar Archivo
                    </Button>
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Nombre completo - Obligatorio */}
              <div className="space-y-2">
                <Label htmlFor="nombreCompleto" className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Nombre Completo <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="nombreCompleto"
                  type="text"
                  value={nombreCompleto}
                  onChange={(e) => {
                    setNombreCompleto(e.target.value);
                    if (errors.nombreCompleto) {
                      setErrors(prev => ({ ...prev, nombreCompleto: "" }));
                    }
                  }}
                  placeholder="Ingrese el nombre completo"
                  className={`w-full text-sm sm:text-base ${errors.nombreCompleto ? "border-red-500" : ""}`}
                  required
                />
                {errors.nombreCompleto && (
                  <p className="text-xs sm:text-sm text-red-500">{errors.nombreCompleto}</p>
                )}
              </div>

              {/* Torre - Obligatorio */}
              <div className="space-y-2">
                <Label htmlFor="torre" className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Torre <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="torre"
                  type="text"
                  value={torre}
                  onChange={(e) => {
                    setTorre(e.target.value);
                    if (errors.torre) {
                      setErrors(prev => ({ ...prev, torre: "" }));
                    }
                  }}
                  placeholder="Ej: A, B, C"
                  className={`w-full text-sm sm:text-base ${errors.torre ? "border-red-500" : ""}`}
                  required
                />
                {errors.torre && (
                  <p className="text-xs sm:text-sm text-red-500">{errors.torre}</p>
                )}
              </div>

              {/* Apartamento - Obligatorio */}
              <div className="space-y-2">
                <Label htmlFor="apartamento" className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Apartamento <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="apartamento"
                  type="text"
                  value={apartamento}
                  onChange={(e) => {
                    setApartamento(e.target.value);
                    if (errors.apartamento) {
                      setErrors(prev => ({ ...prev, apartamento: "" }));
                    }
                  }}
                  placeholder="Ej: 101, 202"
                  className={`w-full text-sm sm:text-base ${errors.apartamento ? "border-red-500" : ""}`}
                  required
                />
                {errors.apartamento && (
                  <p className="text-xs sm:text-sm text-red-500">{errors.apartamento}</p>
                )}
              </div>

              {/* Tipo de documento */}
              <div className="space-y-2">
                <Label htmlFor="tipoDocumento" className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Tipo de Documento
                </Label>
                <select
                  id="tipoDocumento"
                  value={tipoDocumentoCodigo}
                  onChange={(e) => setTipoDocumentoCodigo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm sm:text-base bg-white focus:outline-none focus:ring-2 focus:ring-military-500"
                  disabled={isLoadingCatalogos}
                >
                  <option value="">Seleccione un tipo</option>
                  {tiposDocumento.map((tipo) => (
                    <option key={tipo.tdo_code} value={tipo.tdo_code}>
                      {tipo.tdo_nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Número de documento */}
              <div className="space-y-2">
                <Label htmlFor="numeroDocumento" className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Número de Documento
                </Label>
                <Input
                  id="numeroDocumento"
                  type="text"
                  value={numeroDocumento}
                  onChange={(e) => setNumeroDocumento(e.target.value)}
                  placeholder="Ingrese el número de documento"
                  className="w-full text-sm sm:text-base"
                />
              </div>

              {/* Tipo de vehículo */}
              <div className="space-y-2">
                <Label htmlFor="tipoVehiculo" className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  Tipo de Vehículo
                </Label>
                <select
                  id="tipoVehiculo"
                  value={tipoVehiculo}
                  onChange={(e) => setTipoVehiculo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm sm:text-base bg-white focus:outline-none focus:ring-2 focus:ring-military-500"
                  disabled={isLoadingCatalogos}
                >
                  <option value="">Seleccione un tipo</option>
                  {tiposVehiculo.map((tipo) => (
                    <option key={tipo.tvh_codigo} value={tipo.tvh_codigo}>
                      {tipo.tvh_nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Placa del vehículo */}
              <div className="space-y-2">
                <Label htmlFor="placaVehiculo" className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  Placa del Vehículo
                </Label>
                <Input
                  id="placaVehiculo"
                  type="text"
                  value={placaVehiculo}
                  onChange={(e) => setPlacaVehiculo(e.target.value.toUpperCase())}
                  placeholder="Ej: ABC123"
                  className="w-full text-sm sm:text-base"
                />
              </div>

              {/* Celular */}
              <div className="space-y-2">
                <Label htmlFor="celular" className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Celular <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="celular"
                  type="tel"
                  value={celular}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setCelular(value);
                    if (errors.celular) {
                      setErrors(prev => ({ ...prev, celular: "" }));
                    }
                  }}
                  placeholder="Ej: 3145544566"
                  className={`w-full text-sm sm:text-base ${errors.celular ? "border-red-500" : ""}`}
                  required
                />
                {errors.celular && (
                  <p className="text-xs sm:text-sm text-red-500">{errors.celular}</p>
                )}
              </div>

              {/* Motivo de visita - Obligatorio */}
              <div className="space-y-2">
                <Label htmlFor="motivoVisita" className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Motivo de la Visita <span className="text-red-500">*</span>
                </Label>
                <textarea
                  id="motivoVisita"
                  value={motivoVisita}
                  onChange={(e) => {
                    setMotivoVisita(e.target.value);
                    if (errors.motivoVisita) {
                      setErrors(prev => ({ ...prev, motivoVisita: "" }));
                    }
                  }}
                  placeholder="Ingrese el motivo de la visita"
                  rows={3}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm sm:text-base resize-none focus:outline-none focus:ring-2 focus:ring-military-500 ${errors.motivoVisita ? "border-red-500" : ""}`}
                  required
                />
                {errors.motivoVisita && (
                  <p className="text-xs sm:text-sm text-red-500">{errors.motivoVisita}</p>
                )}
              </div>

              {/* Botones */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <Button
                  type="button"
                  onClick={() => navigate("/visitantes")}
                  variant="outline"
                  className="w-full sm:w-auto text-sm sm:text-base"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:flex-1 bg-military-500 hover:bg-military-600 text-white text-sm sm:text-base"
                  disabled={isSubmitting || isLoadingCatalogos}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar Visitante
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Modal de cámara */}
        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-military-600">
                  Tomar Foto
                </h3>
                <button
                  onClick={stopCamera}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="relative bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-auto max-h-[60vh]"
                  />
                  <canvas ref={canvasRef} className="hidden" />
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={capturePhoto}
                    className="bg-military-500 hover:bg-military-600 text-white"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Capturar Foto
                  </Button>
                  <Button
                    onClick={stopCamera}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

